import { Divider } from '@mui/material'
import { useMemo } from 'react'
import { LIMITS } from '@weblite/permission'
import { resetSection, usePanelHistoryTop } from 'weblite-router'
import {
  UserListItemSkeleton,
  SelectableUserListItemWithStatus,
} from '$common/user-list-item'
import { EmptyStateList } from '$common/empty-state-list'
import { UserChip } from '$common/user-chip'
import { withMultiSelectSearchableListPage } from '$common/multi-select-searchable-list-page'
import { showAddMemberModal } from '$comp/modals/confirmation-dialog'
import { closeAddHallMembers } from '$logic/router'
import {
  useNotSubscribedFamiliarUserIds,
  useSearchedNonMemberFamiliarUserIds,
} from '$logic/users'
import { t } from '$i18n'
import { openRoom } from '$logic/opened-room'
import { getRoom } from '$logic/room'
import { useIsDesktopSize } from '$common/react-hooks/layout'

const ListItem = ({
  id,
  disabled,
  selected,
  onSelectedChange,
}: AddMemberItem & {
  selected: boolean
  onSelectedChange: (isSelected: boolean) => unknown
}) => (
  <>
    <SelectableUserListItemWithStatus
      userId={id}
      selected={selected}
      isPassive={disabled}
      hasDivider={false}
      onSelectedChange={onSelectedChange}
    />
    <Divider variant="inset" />
  </>
)

export interface AddMemberItem {
  id: string
  disabled: boolean
}

export function AddMembers({
  roomId,
  skippable,
}: {
  roomId: string
  skippable: boolean
}) {
  const topPanel = usePanelHistoryTop('roomProfile')
  const isDesktop = useIsDesktopSize()
  const onComplete = async () => {
    const room = await getRoom(roomId)
    analite('CHAT_ADD_MEMBER', {
      type: room!.type,
      source: 'ADD_MEMBERS',
    })
    await closeAddHallMembers()
    isDesktop && skippable && resetSection('left', 'home')

    if (skippable) {
      await openRoom(roomId)
      !isDesktop && resetSection('left', 'home')
    }
  }

  const Page = useMemo(() => {
    return withMultiSelectSearchableListPage<
      AddMemberItem,
      string,
      { query: string; items: AddMemberItem[] }
    >({
      appBarTitle: t('addMembers.title'),
      skippable,
      selectionLimit: LIMITS.addMembersCount,
      SkeletonItem: UserListItemSkeleton,
      EmptyStateComponent: () => <EmptyStateList />,
      fetchHook: () => useNotSubscribedFamiliarUserIds(roomId, 'addMembers'),
      searchHook: useSearchedNonMemberFamiliarUserIds,
      ChipComponent: UserChip,
      selectionPrimaryKey: 'id',
      onBack: closeAddHallMembers,
      onSkip: onComplete,
      onConfirm: selectedItems =>
        showAddMemberModal(
          roomId,
          selectedItems.map(value => value.id),
          onComplete,
        ),
    })
  }, [roomId, skippable, topPanel])

  return (
    <Page
      fetchHookParams={roomId}
      getSearchHookParams={(query, items) => ({ query, items })}
      ListItem={ListItem}
    />
  )
}
